import { fetchJSON as fetchAPI } from './client';

export type ReviewDashboardSnapshot = {
  tenant_id: string; overall_review_score: number; screens_audited: number; screens_total: number;
  avg_usability_score: number; avg_accessibility_score: number; issues_critical: number;
  issues_high: number; redesign_items: number; workflow_friction_points: number; improvement_trend: string;
};

export type ScreenScore = {
  screen_key: string; screen_name: string; usability: number; accessibility: number;
  visual_quality: number; business_value: number; speed: number; clarity: number;
  cognitive_load: number; actionability: number; trust: number; overall_grade: string;
};

export type WorkflowStep = { step: string; friction?: string; friction_score: number; clicks: number };

export type WorkflowReviewSnapshot = {
  steps: WorkflowStep[]; total_clicks: number; friction_points: number; improvement_pct: number;
};

export type PriorityItem = { id: string; issue: string; screen: string; priority: string };

export type RedesignItem = {
  id: string; problem: string; evidence: string; suggested_improvement: string;
  expected_impact: string; effort: string; priority: string; screen: string;
};

export type ScreenEntry = { key: string; name: string; category: string; purpose: string; status: string };

export type CustomerValueReviewSnapshot = {
  roi_in_30_seconds: boolean; executive_clarity: boolean; simplification_needed: boolean;
  trust_score: number; presentation_ready: boolean;
};

export type DashboardReviewSnapshot = {
  what_changed_in_5sec: boolean; what_matters_in_5sec: boolean; what_to_do_in_5sec: boolean;
  why_visible: boolean; expected_value_visible: boolean; pass_rate_pct: number;
};

const COMMAND_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/command';
const fetchC = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const screenReviewApi = {
  dashboard: () => fetchC<ReviewDashboardSnapshot>('/screen-review/dashboard'),
  screens: () => fetchC<{ screens: ScreenEntry[] }>('/screen-review/screens').then((r) => r.screens),
  screenScores: () => fetchC<{ scores: ScreenScore[] }>('/screen-review/screen-scores').then((r) => r.scores),
  workflow: () => fetchC<WorkflowReviewSnapshot>('/screen-review/workflow-review'),
  priorityMatrix: () => fetchC<{ items: PriorityItem[] }>('/screen-review/priority-matrix').then((r) => r.items),
  redesignPlan: () => fetchC<{ items: RedesignItem[] }>('/screen-review/redesign-plan').then((r) => r.items),
  dashboardReview: () => fetchC<DashboardReviewSnapshot>('/screen-review/dashboard-review'),
  customerValueReview: () => fetchC<CustomerValueReviewSnapshot>('/screen-review/customer-value-review'),
};
